import { ClipService } from '@/modules/databaseService/types';
import type { RendererBridge } from '@/modules/rendererBridge';
import { SupabaseClipInstance, SupabaseClipType, CLIP_CATEGORIES, CLIP_SOURCES, MediaClipOverrides } from '@/types/clipTypes';
import { convertToClip } from '@/utils/dataConverters';
import { captureError } from '@/utils/sentry';
import { v1, toResult, type V1Result } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { clipTypeToLegacy, type ClipTypeDto } from '@/infrastructure/api/v1/mappers/clipTypes';
import {
  clipInstanceToLegacy,
  clipInstanceFromLegacy,
  clipInstanceCreateBody,
  clipInstancePatchBody,
  type ClipInstanceDto,
  type LegacyClipInstance,
} from '@/infrastructure/api/v1/mappers/clipInstances';

import type { WriteScope } from '@/modules/undoRedo';
const clipTypeRow = (dto: ClipTypeDto) => clipTypeToLegacy(dto) as unknown as SupabaseClipType;
export const clipInstanceRow = (dto: ClipInstanceDto) => clipInstanceToLegacy(dto) as unknown as SupabaseClipInstance;
export const clipInstanceBody = (row: SupabaseClipInstance) => clipInstanceCreateBody(row as unknown as Partial<LegacyClipInstance>);

/** The clip_types rows shown in the library (display flag on), in creation order. */
async function displayedClipTypes(): Promise<SupabaseClipType[]> {
  const { data, error } = await listAll(page => v1.GET('/api/v1/clip-types', { params: { query: { displayOn: true, ...page } } }));
  if (error) throw error;
  return data.map(clipTypeRow);
}

/** Changes one clip instance; the row comes back in an array, as the generic route's update().select() gave it. */
async function patchClipInstance(clipInstanceId: string, changes: Partial<SupabaseClipInstance>, scope?: WriteScope): Promise<V1Result<SupabaseClipInstance[]>> {
  const { data, error } = await toResult(v1.PATCH('/api/v1/clip-instances/{uniqueId}', {
    params: { path: { uniqueId: clipInstanceId } },
    body: clipInstancePatchBody(changes as unknown as Partial<LegacyClipInstance>),
    headers: scope?.headers,
  }));
  return error ? { data: null, error } : { data: [clipInstanceRow(data)], error: null };
}

/** Creates one clip instance and returns its row. */
async function createClipInstanceRow(row: SupabaseClipInstance, scope?: WriteScope): Promise<SupabaseClipInstance> {
  const { data, error } = await toResult(v1.POST('/api/v1/clip-instances', { body: clipInstanceBody(row), headers: scope?.headers }));
  if (error) throw error;
  return clipInstanceRow(data);
}

// Shared with AssetAdapter's placement create: the media clip type ids, looked up once per session
const mediaClipTypeIds = new Map<'audio' | 'video', string>();

/** The id of the stock audio or video playback clip type. */
export async function mediaClipTypeId(kind: 'audio' | 'video'): Promise<string> {
  const cached = mediaClipTypeIds.get(kind);
  if (cached) return cached;

  const sourceName = kind === 'audio' ? CLIP_SOURCES.AUDIO : CLIP_SOURCES.VIDEO;
  const category = kind === 'audio' ? CLIP_CATEGORIES.AUDIO : CLIP_CATEGORIES.VIDEO;
  let id: string | null;
  try {
    const { data, error } = await toResult(v1.GET('/api/v1/clip-types', {
      params: { query: { sourceName, category: [category], limit: 1 } },
    }));
    if (error) throw error;
    id = data.items[0]?.uniqueId ?? null;
  } catch (error) {
    captureError(error, { domain: 'clip', operation: 'getClipTypeIdBySourceNameAndCategory', sourceName, category });
    console.error(`Error fetching clip type by source name (${sourceName}) and category (${category}):`, error);
    throw error;
  }

  if (!id) {
    throw new Error(`Failed to fetch ${kind} clip type ID`);
  }
  mediaClipTypeIds.set(kind, id);
  return id;
}

/** The clip_instances row for an audio or video asset's playback clip. */
export function mediaClipInstanceRow(
  kind: 'audio' | 'video',
  clipTypeId: string,
  assetInstanceId: string,
  sceneInstanceId: string,
  duration: number,
  sourceName: string,
  beginTime?: number,
  media?: MediaClipOverrides
): SupabaseClipInstance {
  const settings: Record<string, unknown> = { sourceDuration: media?.sourceDuration ?? duration };
  // Written even when 0. A truthy check made "never trimmed" and "trimmed to the
  // start" identical in the data, which blocks any audit of affected scenes.
  if (media) settings.sourceIn = media.sourceIn ?? 0;

  return {
    clip_instance_id: crypto.randomUUID(),
    scene_instance_id: sceneInstanceId,
    asset_instance_id: assetInstanceId,
    clip_type_id: clipTypeId,
    clip_instance_category: kind === 'audio' ? CLIP_CATEGORIES.AUDIO : CLIP_CATEGORIES.VIDEO,
    clip_instance_source_name: sourceName,
    clip_instance_display_name: sourceName,
    clip_instance_duration: duration,
    clip_instance_begin_time: beginTime ?? 0,
    clip_instance_end_time: (beginTime ?? 0) + duration,
    clip_instance_payload: {},
    clip_instance_settings: settings,
    ...(media?.volume !== undefined ? { clip_instance_volume: media.volume } : {}),
  };
}

export class ClipAdapter implements ClipService {
  private awaitClipTypeCache: SupabaseClipType | null = null;

  constructor(
    private readonly bridge: RendererBridge
  ) {}

  private sendClipInstanceUpdated(row: SupabaseClipInstance): void {
    this.bridge.send({ type: 'clipInstanceUpdated', clipInstance: clipInstanceFromLegacy(row as unknown as LegacyClipInstance) });
  }

  async fetchClipTypeRow(clipTypeId: string) {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/clip-types/{clipTypeUniqueId}', { params: { path: { clipTypeUniqueId: clipTypeId } } }));

      if (error) throw error;
      return clipTypeRow(data);
    } catch (error) {
      captureError(error, { domain: 'clip', operation: 'fetchClipTypeRow', clipTypeId });
      console.error('Error in fetchClipTypeRow:', error);
      throw error;
    }
  }

  async fetchAwaitClipType(): Promise<SupabaseClipType | null> {
    if (this.awaitClipTypeCache) return this.awaitClipTypeCache;
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/clip-types', {
        params: { query: { category: [CLIP_CATEGORIES.TRIGGER], limit: 1 } },
      }));
      if (error) throw error;
      const row = data.items[0] ? clipTypeRow(data.items[0]) : null;
      if (row) this.awaitClipTypeCache = row;
      return row;
    } catch (error) {
      captureError(error, { domain: 'clip', operation: 'fetchAwaitClipType' });
      console.error('Error in fetchAwaitClipType:', error);
      return null;
    }
  }

  async fetchAllSceneClips(sceneId: string) {
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/clip-instances', {
        params: { query: { sceneUniqueId: [sceneId], ...page } },
      }));

      if (error) throw error;
      return data.map(clipInstanceRow);
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  /** Stock only: scene_instance_id IS NULL. */
  async fetchClips() {
    try {
      // v1 has no scene filter on clip types, so the scene test runs here on the displayed set
      const rows = await displayedClipTypes();
      return rows.filter(row => row.scene_instance_id == null).map(convertToClip);
    } catch (error) {
      console.error('[ClipAdapter] fetchClips exception:', error);
      throw new Error((error as Error).message, { cause: error });
    }
  }


  /**
   * Animation clip types (non-snap) for the Animations picker, scoped to the
   * selected asset's type: stock (asset_type_id IS NULL) then imported clips
   * matching that asset_type_id. When assetTypeId is null, only stock is returned.
   */
  async fetchAnimationClipTypesForAsset(assetTypeId: string | null): Promise<ReturnType<typeof convertToClip>[]> {
    try {
      const animationClips = (query: { stock?: boolean; assetTypeUniqueId?: string[] }) =>
        listAll(page => v1.GET('/api/v1/clip-types', {
          params: { query: { displayOn: true, excludeCategory: [CLIP_CATEGORIES.SNAP], ...query, ...page } },
        }));
      const [stock, imported] = await Promise.all([
        animationClips({ stock: true }),
        assetTypeId ? animationClips({ assetTypeUniqueId: [assetTypeId] }) : Promise.resolve({ data: [] as ClipTypeDto[], error: null }),
      ]);
      if (stock.error) throw stock.error;
      if (imported.error) throw imported.error;
      return [...stock.data, ...imported.data].map(dto => convertToClip(clipTypeRow(dto)));
    } catch (error) {
      console.error('[ClipAdapter] fetchAnimationClipTypesForAsset exception:', error);
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async addClipInstanceRow(newClipInstance: SupabaseClipInstance, scope?: WriteScope) {
    try {
      const row = await createClipInstanceRow(newClipInstance, scope);
      this.bridge.send({ type: 'clipInstanceAdded', clipInstance: clipInstanceFromLegacy(row as unknown as LegacyClipInstance), restored: false });
      return row;
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async deleteClipInstance(clipInstanceId: string, scope?: WriteScope) {
    try {
      // A clip already gone is deleted, as the generic route's filtered delete treated it
      const { error } = await toResult(v1.DELETE('/api/v1/clip-instances/{uniqueId}', { params: { path: { uniqueId: clipInstanceId } }, headers: scope?.headers }));

      if (error && error.status !== 404) throw error;

      // Once the row is gone the renderer drops the clip, after any save of it answered before
      this.bridge.send({ type: 'clipInstanceRemoved', clipInstanceUniqueId: clipInstanceId });
      return true;
    } catch (error) {
      captureError(error, { domain: 'clip', operation: 'deleteClipInstance', clipInstanceId });
      console.error('Error deleting clip:', error);
      throw error;
    }
  }

  async updateClipInstanceTime(clipInstanceId: string, beginTime: number, endTime: number, speed?: number, scope?: WriteScope) {
    return this.writeClipInstanceTime(clipInstanceId, beginTime, endTime, speed, undefined, scope);
  }

  async updateClipInstanceTimeAndSettings(clipInstanceId: string, beginTime: number, endTime: number, speed: number | undefined, settings: Record<string, unknown>, scope?: WriteScope) {
    return this.writeClipInstanceTime(clipInstanceId, beginTime, endTime, speed, settings, scope);
  }

  private async writeClipInstanceTime(clipInstanceId: string, beginTime: number, endTime: number, speed: number | undefined, settings: Record<string, unknown> | undefined, scope?: WriteScope) {
    try {
      const updateData = {
        clip_instance_begin_time: beginTime,
        clip_instance_end_time: endTime,
        // Keep duration consistent with begin/end so UI + Unity don't revert to stale durations after resize/drag.
        clip_instance_duration: Math.max(0, endTime - beginTime),
        ...(speed !== undefined && { clip_instance_speed: speed }),
        ...(settings !== undefined && { clip_instance_settings: settings }),
      };

      const { error, data } = await patchClipInstance(clipInstanceId, updateData, scope);

      if (error) {
        console.error('Supabase update error:', {
          clipId: clipInstanceId,
          error,
          data
        });
        throw error;
      }

      // Send the renderer the updated row
      if (data && data[0]) this.sendClipInstanceUpdated(data[0]);

      return data;
    } catch (error) {
      captureError(error, { domain: 'clip', operation: 'updateClipInstanceTime', clipInstanceId });
      console.error('Supabase update failed:', {
        clipId: clipInstanceId,
        error
      });
      throw error;
    }
  }

  // Re-point a clip's reactive-trigger FK (trigger_asset_instance_id) to a new gate.
  // Used by the trigger-type swap: the old gate is replaced by a new asset instance, so every
  // clip bound to the old gate must follow to the new one BEFORE the old row is deleted (the
  // FK is ON DELETE SET NULL, so deleting first would unbind them).
  async updateClipInstanceTrigger(clipInstanceId: string, triggerAssetInstanceId: string | null, scope?: WriteScope) {
    try {
      const { error, data } = await patchClipInstance(clipInstanceId, { trigger_asset_instance_id: triggerAssetInstanceId }, scope);

      if (error) {
        console.error('Supabase update error:', { clipId: clipInstanceId, error, data });
        throw error;
      }

      // Notify the renderer with the updated row so its reactive binding follows the new gate
      if (data && data[0]) this.sendClipInstanceUpdated(data[0]);

      return data;
    } catch (error) {
      captureError(error, { domain: 'clip', operation: 'updateClipInstanceTrigger', clipInstanceId });
      throw error;
    }
  }

  async updateClipInstanceUiElement(clipInstanceId: string, uiElementInstanceId: string, scope?: WriteScope): Promise<SupabaseClipInstance[]> {
    const { error, data } = await patchClipInstance(clipInstanceId, { ui_element_instance_id: uiElementInstanceId }, scope);
    if (error) {
      captureError(error, { domain: 'clip', operation: 'updateClipInstanceUiElement', clipInstanceId });
      throw error;
    }
    // The renderer moves the clip to the screen message it now belongs to
    this.sendClipInstanceUpdated(data[0]);
    return data;
  }

  async updateClipInstanceSettings(clipInstanceId: string, settings: any, scope?: WriteScope) {
    try {
      const updateData = {
        clip_instance_settings: settings
      };

      const { error, data } = await patchClipInstance(clipInstanceId, updateData, scope);

      if (error) {
        console.error('Supabase update error:', {
          clipId: clipInstanceId,
          error,
          data
        });
        throw error;
      }

      // Send the renderer the updated row
      if (data && data[0]) this.sendClipInstanceUpdated(data[0]);

      return data;
    } catch (error) {
      console.error('Supabase update failed:', {
        clipId: clipInstanceId,
        error
      });
      throw error;
    }
  }

  async updateClipInstanceSpeed(clipInstanceId: string, speed: number, scope?: WriteScope) {
    try {
      const updateData = {
        clip_instance_speed: speed
      };

      const { error, data } = await patchClipInstance(clipInstanceId, updateData, scope);

      if (error) {
        console.error('Supabase update error:', {
          clipId: clipInstanceId,
          error,
          data
        });
        throw error;
      }

      // Send the renderer the updated row
      if (data && data[0]) this.sendClipInstanceUpdated(data[0]);

      return data;
    } catch (error) {
      console.error('Supabase update failed:', {
        clipId: clipInstanceId,
        error
      });
      throw error;
    }
  }

  async updateClipInstanceVolume(clipInstanceId: string, volume: number, scope?: WriteScope) {
    try {
      const { data, error } = await patchClipInstance(clipInstanceId, { clip_instance_volume: volume }, scope);

      if (error) {
        console.error('Supabase update volume error:', error);
        throw error;
      }

      // Send the renderer the updated row (like other update methods)
      if (data && data[0]) this.sendClipInstanceUpdated(data[0]);

      return data;
    } catch (error) {
      console.error('Supabase update volume failed:', {
        clipId: clipInstanceId,
        volume,
        error
      });
      throw error;
    }
  }

  async updateClipInstanceDisplayName(clipInstanceId: string, displayName: string, scope?: WriteScope) {
    try {
      const { data, error } = await patchClipInstance(clipInstanceId, { clip_instance_display_name: displayName }, scope);

      if (error) {
        console.error('Supabase update display name error:', {
          clipId: clipInstanceId,
          displayName,
          error
        });
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Supabase update display name failed:', {
        clipId: clipInstanceId,
        displayName,
        error
      });
      throw error;
    }
  }
}
